import {
  Avatar,
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import { parseEvents } from "./lib";
import { Event } from "./types";

import { useKeycloak } from "@react-keycloak/web";
import { baseAtom } from "../../../stores/jotai/base";
import { useGetGoogleEvents } from "../../../tanstackQuery/social-integrations/handle-google";
import { zoomIntegrationStateAtom } from "../../../stores/jotai/calender&Zoom";
import IconZoomPermissionError from "../../../components/icons/IconZoomPermissionError";
import IconNoEvent from "../../../components/icons/IconNoEvent";

const GoogleLoggedInWithoutZoom = ({ onConnectZoom }) => {
  const theme = useTheme();
  const { keycloak } = useKeycloak();
  const base = useAtomValue(baseAtom);

  const [events, setEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const session = useAtomValue(discoverySessionAtom);
  const [zoomIntegration, setZoomIntegration] = useAtom(
    zoomIntegrationStateAtom
  );

  const {
    data: fetchedEvents,
    isLoading: eventsLoading,
    isRefetching: eventsRefetching,
    refetch: eventsRefetch
  } = useGetGoogleEvents(
    { accessToken: keycloak.token },
    {
      teamId: base?.team?.id,
      userId: base?.user?.userID
    },
    {
      enabled: !!(base?.team?.id && base?.user?.userID)
      // refetchOnWindowFocus: false
    }
  );

  const fetchGoogleEvents = async () => {
    setIsLoadingEvents(true);
    try {
      const parsedEvents = parseEvents(fetchedEvents);
      setEvents(parsedEvents);
    } catch (error: any) {
      console.error("Error fetching events:", error);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const eventsGroupedByDay = events?.reduce<Record<string, Event[]>>(
    (acc, event) => {
      const dayKey = `${event.day} ${event.month}, ${event.date} `;
      if (!acc[dayKey]) {
        acc[dayKey] = [];
      }
      acc[dayKey]?.push(event);
      return acc;
    },
    {}
  );

  useEffect(() => {
    async function refetchingEvents() {
      if (zoomIntegration.eventsRefetching) {
        await eventsRefetch();
        setZoomIntegration((prev) => ({
          ...prev,
          eventsRefetching: false,
          eventsRefetchingWithPubnub: false
        }));
      }
    }

    refetchingEvents();
  }, [zoomIntegration.eventsRefetching]);

  useEffect(() => {
    if (fetchedEvents?.length) fetchGoogleEvents();
  }, [session, fetchedEvents]);

  const isAllEventsLoading =
    isLoadingEvents ||
    eventsLoading ||
    (zoomIntegration.eventsRefetching && eventsRefetching);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "space-between",
        gap: 1,
        bgcolor: "white",
        pt: 1,
        pb: 3,
        borderRadius: 1,
        boxShadow: 1
      }}
    >
      <Box>
        {isAllEventsLoading ? (
          <LinearProgress />
        ) : (
          <Stack
            direction="column"
            sx={{
              width: "100%",
              mt: 1,
              height: "270px",
              overflowY: "auto",
              borderBottom: "1px solid #E4E4E7",
              pb: 1
            }}
          >
            {Object.entries(eventsGroupedByDay || {}).length > 0 ? (
              Object.entries(eventsGroupedByDay || {}).map(
                ([dayDate, dayEvents]) => (
                  <Box key={dayDate}>
                    <Stack
                      direction="row"
                      sx={{
                        width: "100%",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        my: 2
                      }}
                      px={2}
                    >
                      <Avatar
                        sx={{
                          width: 35,
                          height: 35,
                          bgcolor: dayEvents?.[0]?.isToday
                            ? theme.custom.mainButtonHighlight
                            : theme.custom.gray6,
                          marginRight: 1
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "600",
                            color: dayEvents?.[0]?.isToday
                              ? theme.custom.white
                              : "#595959",
                            borderRadius: "500px",
                            padding: "12px"
                          }}
                        >
                          {dayEvents?.[0].date}
                        </Typography>
                      </Avatar>

                      <Typography
                        sx={{
                          fontWeight: "600",
                          color: dayEvents?.[0]?.isToday
                            ? theme.custom.mainButtonHighlight
                            : "#595959"
                        }}
                      >{`${dayEvents?.[0]?.month}, ${dayEvents?.[0]?.day}`}</Typography>
                    </Stack>
                    <Stack spacing={2}>
                      {dayEvents?.map((event) => (
                        <Box
                          px={3}
                          key={event?.id}
                          sx={{
                            display: "flex",
                            flexDirection: "column"
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: "600",
                              color: "#595959"
                            }}
                          >
                            {event?.title}
                          </Typography>
                          <Typography sx={{ color: "#A1A1AA" }}>
                            {event?.time}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )
              )
            ) : (
          <Stack direction={"column"} sx={{
              flex: 1,                
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              }}>
              <IconNoEvent />
              <Typography mt={2} sx={{
                fontSize:"15px",
                color:theme.palette.primary.main,
                fontWeight:theme.typography.fontWeightBold
              }}>
                No Event Found!
              </Typography>
            </Stack>
            )}
          </Stack>
        )}
      </Box>

      <Box sx={{ width: "100%", textAlign: "center" }}>
        <Button
          onClick={onConnectZoom}
          variant="contained"
          sx={{
            bgcolor: "#006AC6",
            "&:hover": { bgcolor: "#0053a0" }
          }}
        >
          Connect Zoom
        </Button>
      </Box>
    </Box>
  );
};

export default GoogleLoggedInWithoutZoom;
